import React, { useMemo, useState, useCallback, useEffect } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import TextField from "@mui/material/TextField";

/* -------------------------------- ERROR BOUNDARY -------------------------------- */
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("DataGrid Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-red-500">
          Something went wrong rendering the table. Please try refreshing.
        </div>
      );
    }

    return this.props.children;
  }
}

/* -------------------------------- DRAGGABLE HEADER -------------------------------- */

const DraggableHeaderCell = ({
  headerName,
  onMoveColumn,
  columnIndex,
}: {
  headerName: string;
  onMoveColumn: (from: number, to: number) => void;
  columnIndex: number;
}) => {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "COLUMN",
      item: { index: columnIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [columnIndex]
  );

  const [, drop] = useDrop(
    () => ({
      accept: "COLUMN",
      hover: (dragged: { index: number }) => {
        if (dragged.index !== columnIndex) {
          onMoveColumn(dragged.index, columnIndex);
          dragged.index = columnIndex;
        }
      },
    }),
    [columnIndex, onMoveColumn]
  );

  return (
    <div
      ref={(node) => drag(drop(node))}
      style={{
        cursor: "move",
        opacity: isDragging ? 0.5 : 1,
        userSelect: "none",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        padding: "0 8px",
        backgroundColor: isDragging ? "rgba(0, 0, 0, 0.04)" : "transparent",
        transition: "background-color 0.2s",
      }}
    >
      {headerName || ""}
    </div>
  );
};

/* ----------------------------------- DATATABLE ------------------------------------ */

interface Row {
  id: number | string;
  [key: string]: any;
}

const DataTable = ({ rawData }: { rawData: unknown }) => {
  /* Ensure `rows` is a real array of objects and every row has a unique `id` */
  const rows: Row[] = useMemo(() => {
    // Handle string input that might contain markdown text followed by JSON
    if (typeof rawData === "string") {
      try {
        // Find the first occurrence of '[' which marks the start of the JSON array
        const jsonStartIndex = rawData.indexOf("[");
        if (jsonStartIndex === -1) {
          // If no array found, try parsing the whole string
          return JSON.parse(rawData).map((row: any, idx: any) => ({
            id: row.id ?? idx,
            ...row,
          }));
        }

        // Extract just the JSON array part
        const jsonStr = rawData.slice(jsonStartIndex);
        const data = JSON.parse(jsonStr);
        return data.map((row: any, idx: any) => ({
          id: row.id ?? idx,
          ...row,
        }));
      } catch (error) {
        console.error("Error parsing data:", error);
        return [];
      }
    }

    // Handle non-string input (already parsed JSON)
    const data = Array.isArray(rawData) ? rawData : [];
    return data.map((row: any, idx: any) => ({
      id: row.id ?? idx,
      ...row,
    }));
  }, [rawData]);

  /*  Build column list (omit id column for display) */
  const columns = useMemo<GridColDef[]>(() => {
    if (!rows.length) return [];

    return Object.keys(rows[0])
      .filter((key) => key !== "id")
      .map((key) => ({
        field: key,
        headerName: key,
        flex: 1,
        minWidth: 150,
        sortable: true,
        filterable: true,
      }));
  }, [rows]);

  /*  Column drag-and-drop re-ordering */
  const [columnOrder, setColumnOrder] = useState<number[]>([]);

  // Initialize column order when columns change
  useEffect(() => {
    setColumnOrder(columns.map((_, index) => index));
  }, [columns]);

  const handleColumnOrderChange = useCallback((from: number, to: number) => {
    setColumnOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const draggableColumns = useMemo<GridColDef[]>(() => {
    if (!columns.length || !columnOrder.length) return columns;

    const orderedColumns: GridColDef[] = [];

    for (const colIndex of columnOrder) {
      const col = columns[colIndex];
      if (!col) continue;

      orderedColumns.push({
        ...col,
        renderHeader: () => (
          <DraggableHeaderCell
            headerName={col.headerName || col.field}
            columnIndex={colIndex}
            onMoveColumn={handleColumnOrderChange}
          />
        ),
      });
    }

    return orderedColumns;
  }, [columns, columnOrder, handleColumnOrderChange]);

  /*  Simple text filter */
  const [filterText, setFilterText] = useState("");
  const filteredRows = useMemo(
    () =>
      rows.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(filterText.toLowerCase())
        )
      ),
    [rows, filterText]
  );

  /* Render */
  return (
    <ErrorBoundary>
      <div className="flex flex-col h-full">
        <TextField
          label="Filter"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />

        <DndProvider backend={HTML5Backend}>
          <div style={{ height: "calc(100vh - 240px)", width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={draggableColumns}
              getRowId={(row) => row.id}
              density="compact"
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "transparent",
                },
                "& .MuiDataGrid-columnHeader": {
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                },
                "& .MuiDataGrid-columnHeaderDraggableContainer": {
                  cursor: "move",
                },
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
                "& .MuiDataGrid-menuIcon": {
                  opacity: 0.6,
                  "&:hover": {
                    opacity: 1,
                  },
                },
              }}
            />
          </div>
        </DndProvider>
      </div>
    </ErrorBoundary>
  );
};

export default DataTable;
